import {Button, Col, Form, Modal, Row, Spinner} from 'react-bootstrap'
import Select from 'react-select'
import {useFormik} from 'formik'
import {ChangeEvent, useEffect, useState} from 'react'
import {useQuery} from '@apollo/client'
import {GET_DOCUMENTS_POLICY} from '../../../gql/queries/policiesApiQueries'
import {IResAllTagsDocuments} from '../../../types/policiesApi'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {UPDATE_RESOURCE_CENTER_STR} from '../../../gql/mutations/resourceCenterMutations'
import {parseId} from '../../../helpers'
import {RESOURCE_TYPE_OPTIONS} from '../resourceTypeHelpers'
import {IResourceCenter, IUpdateResourceCenterResponse} from '../../../types/resourceCenter'
import {IResourceCategoryTreeResponse} from '../../../types/resourceCategory'
import {GET_RESOURCE_CATEGORY_TREE} from '../../../gql/queries/resourceCategoryQuery'
import {flattenCategoryTree} from '../categoryTreeHelpers'
import {prependBackendUrl} from '../../../gql/client'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  resourceType: Yup.string().required('Resource type is required'),
  url: Yup.string().when('resourceMode', {
    is: 'url',
    then: (schema) => schema.required('URL is required when using URL mode').url('Must be a valid URL'),
    otherwise: (schema) => schema.notRequired(),
  }),
})

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  resource: IResourceCenter
}

export const ModalEditResource: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  resource,
}) => {
  const [uploadFile, {data, error, loading}] = useUploadFile<IUpdateResourceCenterResponse>()
  const [newFile, setNewFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)

  const {
    data: dataTags,
    loading: loadingTags,
    error: errorTags,
  } = useQuery<IResAllTagsDocuments>(GET_DOCUMENTS_POLICY, {
    fetchPolicy: 'cache-first',
  })

  // El modal consulta el árbol directamente (cache-and-network) para que las
  // carpetas recién creadas aparezcan siempre, sin depender del estado de la página
  const {data: dataCategories, loading: loadingCategories} =
    useQuery<IResourceCategoryTreeResponse>(GET_RESOURCE_CATEGORY_TREE, {
      fetchPolicy: 'cache-and-network',
    })

  const formik = useFormik({
    initialValues: {
      name: resource.name,
      description: resource.description || '',
      url: resource.url || '',
      resourceMode: (resource.url ? 'url' : 'file') as 'file' | 'url',
      resourceType: resource.resourceType,
      documentTagId: resource.documentTag?.id || '',
      categoryId: resource.category?.id || '',
      comments: resource.comments || '',
      isActive: resource.isActive,
      order: resource.order,
    },
    validationSchema,
    onSubmit: async (v) => {
      const formdata = new FormData()

      // Solo agregar archivo si hay uno nuevo
      if (v.resourceMode === 'file' && newFile) {
        formdata.append('0', newFile, newFile.name)
      }

      const map = (v.resourceMode === 'file' && newFile) ? {'0': ['variables.resourceData.file']} : {}
      const obj = {
        query: UPDATE_RESOURCE_CENTER_STR,
        variables: {
          id: parseId(resource.id),
          resourceData: {
            name: v.name,
            description: v.description || undefined,
            ...(v.resourceMode === 'file' && newFile ? {file: null} : {}),
            ...(v.resourceMode === 'url' && v.url ? {url: v.url} : {}),
            resourceType: v.resourceType,
            documentTagId: v.documentTagId ? parseId(v.documentTagId) : null,
            categoryId: v.categoryId ? parseId(v.categoryId) : null,
            comments: v.comments || undefined,
            isActive: v.isActive,
            order: v.order,
          },
        },
      }

      formdata.append('map', JSON.stringify(map))
      formdata.append('operations', JSON.stringify(obj))

      try {
        await uploadFile(formdata)
      } catch (err) {
        console.error('Error updating resource:', err)
        toast.error('Error updating resource')
      }
    },
  })

  // Generate preview for images
  useEffect(() => {
    if (newFile && newFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(newFile)
      setPreview(url)
      return () => URL.revokeObjectURL(url)
    } else if (!newFile && resource.file) {
      // Show current file preview if it's an image
      const ext = resource.file.split('.').pop()?.toLowerCase()
      if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext || '')) {
        setPreview(prependBackendUrl(resource.file))
      }
    } else {
      setPreview(null)
    }
  }, [newFile, resource.file])

  // Handle paste
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!isOpen) return
      if (!e.clipboardData) return

      const files = Array.from(e.clipboardData.files || [])
      if (files.length > 0 && files[0] instanceof File) {
        e.preventDefault()
        setIsDragging(true)
        setNewFile(files[0])
        toast.info('File pasted')
        setTimeout(() => setIsDragging(false), 1000)
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [isOpen])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.toString()}`)
  }, [error])

  useEffect(() => {
    if (errorTags) toast.error(`Error: ${errorTags.message}`)
  }, [errorTags])

  useEffect(() => {
    if (!data) return
    if (data.updateResourceCenter.success) {
      onClose()
      refetch()
      toast.success('Resource updated successfully')
    } else if (
      data.updateResourceCenter.errors &&
      data.updateResourceCenter.errors.length > 0
    ) {
      toast.error(`Error: ${data.updateResourceCenter.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleClose = () => {
    setNewFile(null)
    setPreview(null)
    onClose()
  }

  const currentFileName = resource.file?.split('/').pop()?.split('?')[0] || resource.url || 'No file/URL'

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-pencil-square" /> Edit Resource
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            {/* Current Resource Info */}
            <Col md={12}>
              <div className="alert alert-info mb-3">
                <strong>Current resource:</strong> {currentFileName}
                <br />
                <small className="text-muted">
                  {resource.url ? 'This is a URL resource. ' : 'This is a file resource. '}
                  You can change the type below or keep it as is.
                </small>
              </div>
            </Col>

            {/* Resource Mode Selector */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Resource Type</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="mode-file-edit"
                    name="resourceMode"
                    label="Upload File"
                    checked={formik.values.resourceMode === 'file'}
                    onChange={() => {
                      formik.setFieldValue('resourceMode', 'file')
                      formik.setFieldValue('url', '')
                    }}
                  />
                  <Form.Check
                    type="radio"
                    id="mode-url-edit"
                    name="resourceMode"
                    label="External URL (Loom, YouTube, etc)"
                    checked={formik.values.resourceMode === 'url'}
                    onChange={() => {
                      formik.setFieldValue('resourceMode', 'url')
                      setNewFile(null)
                      setPreview(null)
                    }}
                  />
                </div>
              </Form.Group>
            </Col>

            {/* File Upload (Optional) */}
            {formik.values.resourceMode === 'file' && (
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Replace File (Optional)</Form.Label>
                <div
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault()
                    setIsDragging(false)
                    const files = Array.from(e.dataTransfer.files || [])
                    if (files.length > 0 && files[0] instanceof File) {
                      setNewFile(files[0])
                    }
                  }}
                  className={`border rounded p-3 text-center ${
                    isDragging ? 'bg-light border-primary' : 'border-secondary'
                  }`}
                  style={{cursor: 'pointer', borderStyle: 'dashed'}}
                >
                  {isDragging ? (
                    <span className="text-primary fw-bold">Drop file here...</span>
                  ) : (
                    <span className="text-muted">
                      Drag & drop new file here, paste (Ctrl+V), or click below to select
                    </span>
                  )}
                </div>

                <Form.Control
                  name="newFile"
                  onChange={(e) => {
                    const file = (e as ChangeEvent<HTMLInputElement>)?.target?.files?.[0]
                    if (file) {
                      setNewFile(file)
                    }
                  }}
                  type="file"
                  className="mt-2"
                />

                {(newFile || preview) && (
                  <div className="mt-3 p-3 border rounded bg-light">
                    <div className="d-flex align-items-center gap-3">
                      {preview && (
                        <img
                          src={preview}
                          alt="Preview"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      )}
                      <div className="flex-fill">
                        {newFile ? (
                          <>
                            <div className="fw-bold">{newFile.name}</div>
                            <small className="text-muted">
                              {(newFile.size / 1024).toFixed(2)} KB
                            </small>
                            <div className="badge bg-success ms-2">New file</div>
                          </>
                        ) : (
                          <>
                            <div className="fw-bold">{currentFileName}</div>
                            <div className="badge bg-info">Current file</div>
                          </>
                        )}
                      </div>
                      {newFile && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setNewFile(null)
                            setPreview(null)
                          }}
                        >
                          <i className="bi bi-x-lg" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>
            )}

            {/* URL Input */}
            {formik.values.resourceMode === 'url' && (
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    URL <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="url"
                    type="url"
                    placeholder="https://www.loom.com/share/..."
                    value={formik.values.url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={Boolean(formik.errors.url && formik.touched.url)}
                  />
                  {formik.touched.url && formik.errors.url && (
                    <Form.Text className="text-danger">{formik.errors.url}</Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Supported: Loom, YouTube, Vimeo, or any external link
                  </Form.Text>
                </Form.Group>
              </Col>
            )}

            {/* Name */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  isInvalid={Boolean(formik.errors.name && formik.touched.name)}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  name="name"
                  placeholder="Resource name"
                />
                {formik.errors.name && formik.touched.name && (
                  <Form.Text className="text-danger">{formik.errors.name}</Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* Resource Type */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Resource Type <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  name="resourceType"
                  onBlur={formik.handleBlur}
                  value={RESOURCE_TYPE_OPTIONS.find(
                    (opt) => opt.value === formik.values.resourceType
                  )}
                  onChange={(option) => {
                    formik.setFieldValue('resourceType', option?.value || 'document')
                  }}
                  options={RESOURCE_TYPE_OPTIONS}
                />
                {formik.errors.resourceType && formik.touched.resourceType && (
                  <Form.Text className="text-danger">{formik.errors.resourceType}</Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* Description */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Description</Form.Label>
                <Form.Control
                  name="description"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  as="textarea"
                  rows={2}
                  placeholder="Brief description of the resource..."
                />
              </Form.Group>
            </Col>

            {/* Tag */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Tag</Form.Label>
                <Select
                  isClearable
                  isLoading={loadingTags}
                  value={
                    formik.values.documentTagId
                      ? dataTags?.allAttachmentsTags.edges
                          .map(({node}) => ({
                            label: node.tagName,
                            value: node.id,
                          }))
                          .find((opt) => opt.value === formik.values.documentTagId)
                      : null
                  }
                  onChange={(option) => {
                    formik.setFieldValue('documentTagId', option?.value || '')
                  }}
                  options={
                    dataTags?.allAttachmentsTags.edges.map(({node}) => ({
                      label: node.tagName,
                      value: node.id,
                    })) || []
                  }
                  placeholder="Select a tag..."
                />
              </Form.Group>
            </Col>

            {/* Folder */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Folder</Form.Label>
                <Select
                  isClearable
                  isLoading={loadingCategories}
                  value={
                    flattenCategoryTree(dataCategories?.resourceCategoryTree || []).find(
                      (opt) => opt.value === formik.values.categoryId
                    ) || null
                  }
                  onChange={(option) => {
                    formik.setFieldValue('categoryId', option?.value || '')
                  }}
                  options={flattenCategoryTree(dataCategories?.resourceCategoryTree || [])}
                  placeholder="No folder (uncategorized)"
                />
              </Form.Group>
            </Col>

            {/* Order */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Display Order</Form.Label>
                <Form.Control
                  type="number"
                  name="order"
                  value={formik.values.order}
                  onChange={formik.handleChange}
                  placeholder="0"
                  min="0"
                />
                <Form.Text className="text-muted">
                  Lower numbers appear first (0 = highest priority)
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Comments */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Comments</Form.Label>
                <Form.Control
                  name="comments"
                  onChange={formik.handleChange}
                  value={formik.values.comments}
                  as="textarea"
                  rows={3}
                  placeholder="Additional notes or instructions..."
                />
              </Form.Group>
            </Col>

            {/* Is Active */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active (visible to users)"
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                />
              </Form.Group>
            </Col>

            {/* Stats */}
            <Col md={12}>
              <div className="alert alert-secondary">
                <div className="row text-center">
                  <div className="col">
                    <strong>{resource.viewsCount}</strong>
                    <div className="small text-muted">Total Views</div>
                  </div>
                  <div className="col">
                    <strong>{resource.created ? new Date(resource.created).toLocaleDateString() : 'N/A'}</strong>
                    <div className="small text-muted">Created</div>
                  </div>
                  <div className="col">
                    <strong>{resource.updated ? new Date(resource.updated).toLocaleDateString() : 'N/A'}</strong>
                    <div className="small text-muted">Last Updated</div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={loading || loadingTags}
          onClick={() => formik.handleSubmit()}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Updating...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" />
              Update Resource
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
